import { FormControl, RadioGroup } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import { Callout, CalloutVariant } from "../../../../Callout";
import { DataRecipientLogo } from "../../../../DataRecipientLogo";
import { RadioControlLabel } from "../../../../input/RadioControlLabel";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { useDataMapCollectedPIC } from "../../../../../../common/service/resources/data-map/dataMapTransformers";
import { getSensitivePICsWithFilters } from "../../../../../../common/service/resources/data-map/picHelpers";
import { isRequestContextApplicableWithCrunchFiltering } from "../../../../../util/vendors";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { VendorOutcomeOption } from "../../../../../../common/service/server/Types";
import { CategorizedVendorList } from "../shared/CategorizedVendorList";
import { InstructionsButton, InstructionsContent } from "../shared/CollapsibleInstructions";
import { useInstructionsState } from "../shared/useInstructionsState";
import "../shared/shared-styles.css";

export const USRemoveConsentStep = () => {
  const [org] = usePrimaryOrganization();
  const [allVendors] = useOrganizationVendors(org?.id, undefined, true);

  const { request, setStepCompleted, mutations } = useDataRequestProcessingContext();
  const { data: dataMap } = useDataMap(org.id);
  const instructionsState = useInstructionsState();
  const [vendorOutcomes, setVendorOutcomes] = useState<Record<string, string>>({});

  // Get collected PICs from consumer collection groups only (exclude HR groups)
  const collectedPICs = useDataMapCollectedPIC(dataMap, {
    collectionGroupType: ["BUSINESS_TO_CONSUMER", "BUSINESS_TO_BUSINESS"],
  });

  // Get sensitive PIC IDs (derived from collectedPICs hook result)
  const sensitivePICIds = useMemo(() => {
    if (!dataMap || !collectedPICs || !Array.isArray(collectedPICs)) return [];

    const collectedPICIds = collectedPICs.map((pic) => pic?.id).filter(Boolean);
    const sensitivePICs = getSensitivePICsWithFilters(dataMap, {
      filterPicIds: collectedPICIds,
    });

    return sensitivePICs?.map((pic) => pic.id) || [];
  }, [dataMap, collectedPICs]);

  // Get sensitive PICs with full details for display in Callout
  const sensitivePICsForDisplay = useMemo(() => {
    if (!dataMap || !collectedPICs || !Array.isArray(collectedPICs)) return [];

    const collectedPICIds = collectedPICs.map((pic) => pic?.id).filter(Boolean);
    if (collectedPICIds.length === 0) return [];

    const sensitivePICs = getSensitivePICsWithFilters(dataMap, {
      filterPicIds: collectedPICIds,
    });

    return sensitivePICs || [];
  }, [dataMap, collectedPICs]);

  // Get vendors that process sensitive data for consumer collection groups (excluding HR)
  const sensitiveDataVendors = useMemo(() => {
    if (!dataMap || !allVendors || !sensitivePICIds.length) return [];

    // Find vendors that have disclosures for sensitive PICs in consumer collection groups
    const vendorsWithSensitiveData = allVendors.filter((vendor) => {
      // Check if vendor has any disclosures for sensitive PICs
      const hasDisclosures = dataMap.disclosure.some((disclosure) => {
        const picIsSensitive = sensitivePICIds.includes(disclosure.picId);
        const vendorMatches = disclosure.vendorId === vendor.vendorId;

        // Check if this disclosure is for a consumer collection group (not HR)
        const collectionGroup = dataMap.collectionGroups[disclosure.groupId];
        const isConsumerGroup =
          collectionGroup &&
          (collectionGroup.type === "BUSINESS_TO_CONSUMER" ||
            collectionGroup.type === "BUSINESS_TO_BUSINESS");

        return picIsSensitive && vendorMatches && isConsumerGroup;
      });

      // Apply context filtering
      const contextApplicable = isRequestContextApplicableWithCrunchFiltering(
        dataMap,
        request.context || "CONSUMER",
        vendor,
        request,
        org,
      );

      return hasDisclosures && contextApplicable && !vendor.isInstalled;
    });

    return sortVendorsByCategory(vendorsWithSensitiveData);
  }, [dataMap, allVendors, sensitivePICIds, request, org]);

  // Initialize vendor outcomes from request
  useEffect(() => {
    if (request?.vendorOutcomes) {
      const outcomes = {};
      request.vendorOutcomes.forEach((outcome) => {
        outcomes[outcome.vendor.id] = outcome.outcome;
      });
      setVendorOutcomes(outcomes);
    }
  }, [request?.vendorOutcomes]);

  const handleVendorOutcomeChange = useCallback(
    (vendor, outcome: VendorOutcomeOption) => {
      setVendorOutcomes((prev) => ({ ...prev, [vendor.vendorId]: outcome }));
      mutations.updateVendorOutcomeMutation.mutate({ vendor: vendor.vendorId, outcome });
    },
    [mutations.updateVendorOutcomeMutation],
  );

  // Check if all vendors have been answered
  const allVendorsAnswered = useMemo(() => {
    if (sensitiveDataVendors.length === 0) {
      return true; // No vendors to process, can proceed
    }

    return sensitiveDataVendors.every((vendor) => {
      const outcome = vendorOutcomes[vendor.vendorId];
      return outcome === "NO_DATA" || outcome === "DATA_DELETED";
    });
  }, [sensitiveDataVendors, vendorOutcomes]);

  useEffect(() => {
    // Step is completed when all vendors have been answered
    setStepCompleted(allVendorsAnswered);
  }, [allVendorsAnswered, setStepCompleted]);

  return (
    <>
      <div className="text-t3 mb-std">
        The consumer has withdrawn their consent for your business to process their sensitive
        personal data. Remove this sensitive data from the vendors list below. Take any additional
        internal steps to ensure this consumer's sensitive data is removed and no longer processed
        (stored, analyzed, collected) within your systems.
      </div>

      {sensitivePICsForDisplay.length > 0 ? (
        <Callout
          variant={CalloutVariant.Clear}
          title="Based on your Data Map, your business collects the following Sensitive Personal Information:"
          className="mb-md"
        >
          <ul>
            {sensitivePICsForDisplay.map((pic) => (
              <li key={pic.id}>{pic.name}</li>
            ))}
          </ul>
        </Callout>
      ) : (
        <Callout variant={CalloutVariant.Clear} className="mb-md">
          <div className="text-t3">No sensitive data categories found in your Data Map.</div>
        </Callout>
      )}

      {sensitiveDataVendors.length > 0 && (
        <CategorizedVendorList
          vendors={sensitiveDataVendors}
          renderVendor={(vendor) => {
            const outcome = vendorOutcomes[vendor.vendorId] || "";
            const instruction = vendor.instructions.find((vi) => vi.requestType === "DELETE");

            return (
              <div key={vendor.vendorId} className="border-top--grey pt-std pb-std">
                <div className="flex-container flex--align-start">
                  <div className="text-t4 w-256 flex-container flex--align-start pl-xs">
                    <DataRecipientLogo
                      className="w-space-large align-top mr-mdlg"
                      dataRecipient={vendor}
                    />
                    <span>{vendor.name}</span>
                  </div>
                  <div className="flex-grow">
                    <FormControl component="fieldset" className="mb-md">
                      <RadioGroup
                        className="flex--justify-start gap-xl"
                        aria-label={`Vendor ${vendor.name} sensitive data handling`}
                        style={{ flexWrap: "nowrap" }}
                        name={`vendor-${vendor.vendorId}-sensitive-data-handling`}
                        onChange={(e) =>
                          handleVendorOutcomeChange(vendor, e.target.value as VendorOutcomeOption)
                        }
                        value={outcome}
                        row
                      >
                        <RadioControlLabel
                          value="NO_DATA"
                          label={
                            <span className="text-t2 radio-label-text">No data to delete</span>
                          }
                          recommended={
                            instruction?.processingMethod === "INACCESSIBLE_OR_NOT_STORED"
                          }
                          narrow
                        />
                        <RadioControlLabel
                          value="DATA_DELETED"
                          label={<span className="text-t2 radio-label-text">Data deleted</span>}
                          recommended={instruction?.processingMethod === "DELETE"}
                          narrow
                        />

                        <InstructionsButton
                          vendorId={vendor.vendorId}
                          hasInstructions={Boolean(
                            vendor.deletionInstructions || instruction?.processingInstructions,
                          )}
                          isExpanded={instructionsState.isExpanded(vendor.vendorId)}
                          onToggle={instructionsState.toggleInstructions}
                        />
                      </RadioGroup>
                    </FormControl>
                  </div>
                </div>

                {/* Instructions content appears below the vendor row */}
                <InstructionsContent
                  instructions={vendor.deletionInstructions}
                  processingInstructions={instruction?.processingInstructions}
                  title="Deletion Instructions"
                  isExpanded={instructionsState.isExpanded(vendor.vendorId)}
                />
              </div>
            );
          }}
        />
      )}

      {sensitiveDataVendors.length === 0 && (
        <Callout variant={CalloutVariant.Clear} className="mb-md">
          <div className="text-t3">
            No vendors found that process sensitive data for consumer collection groups.
          </div>
        </Callout>
      )}

      {sensitiveDataVendors.length > 0 && !allVendorsAnswered && (
        <Callout variant={CalloutVariant.Yellow} className="mb-md">
          <div className="text-t3">
            Please indicate the status of sensitive data removal for all vendors above before
            proceeding.
          </div>
        </Callout>
      )}
    </>
  );
};
